import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  ConsoleLogger,
  DefaultDeviceController,
  DefaultMeetingSession,
  LogLevel,
  MeetingSessionConfiguration,
  VideoTileState,
} from 'amazon-chime-sdk-js';

import { RootState } from '../../../store/store';
import {
  getSessionJoinInfo,
  startSession,
  endSession,
  ChimeMeeting,
  ChimeAttendee,
} from '../api/session';
import { JOIN_REFUSAL_COPY, isWaitingRefusal, joinRefusalOf } from '../learner/joinRefusal';
import {
  LocalMedia,
  reducedMediaNotice,
  startCamera,
  startLocalMedia,
} from '../learner/localMedia';
import { RecordingStatus } from '../api/session-evidence';

type Status =
  | 'loading'
  | 'teacher-start'
  | 'waiting'
  | 'joining'
  | 'in-call'
  | 'device-error'
  | 'ended'
  | 'error';

interface TileInfo {
  tileId: number;
  isLocal: boolean;
}

/**
 * Live video session room (ADR-013). Teacher or parent (on behalf of the student)
 * joins an in-progress session via the Amazon Chime SDK. The session is recorded
 * server-side — a banner makes that explicit (transparency for guardians).
 *
 * Presentation is a modern conferencing layout (Meet/Zoom-style): a participant
 * grid, a self-view picture-in-picture, and a floating control bar. While the
 * teacher has not started, the parent view polls and auto-joins the moment the
 * room goes live. A room that IS live but whose media fails to start surfaces an
 * explicit device error (with retry) instead of silently returning to "waiting".
 *
 * NOTE: this is browser-only Chime SDK code validated against a live dev
 * environment (Phase 4); it is type-checked but not exercised in CI.
 */
interface SessionRoomProps {
  /** Where "Volver" goes. The child's space passes its own home, not the catalog. */
  backTo?: string;
  /**
   * Offered after leaving or a failure (STU-10-FR-06). Rejoining asks the
   * server for fresh join info; it creates no second check-in, which is
   * stamped once per session (S08-GT-06).
   */
  onRejoin?: () => void;
  /**
   * STU-10-FR-05 — false when the learner chose to enter with the camera off.
   * The room joins either way; the camera can be turned on from inside.
   */
  startWithCamera?: boolean;
  /**
   * DEC-S3-P09-04: the guardian's own entry from Parent mode. It is their
   * connection, so the learner's attendance does not come from it.
   */
  guardianEntry?: boolean;
}

const SessionRoom: React.FC<SessionRoomProps> = ({
  backTo = '/courses',
  onRejoin,
  startWithCamera = true,
  guardianEntry = false,
}) => {
  const { bookingId } = useParams<{ bookingId: string }>();
  const navigate = useNavigate();
  const role = useSelector((state: RootState) => state.auth.user?.role);

  const [status, setStatus] = useState<Status>('loading');
  const [error, setError] = useState<string | null>(null);
  const [tiles, setTiles] = useState<TileInfo[]>([]);
  const [muted, setMuted] = useState(false);
  const [camOff, setCamOff] = useState(false);
  const [media, setMedia] = useState<LocalMedia | null>(null);
  const [recordingStatus, setRecordingStatus] = useState<RecordingStatus | undefined>();

  const sessionRef = useRef<DefaultMeetingSession | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const videoElsRef = useRef<Map<number, HTMLVideoElement>>(new Map());
  // Guards against overlapping join attempts fired by the waiting poll.
  const joiningRef = useRef(false);

  const isTeacher = role === 'TEACHER';
  const counterpartLabel = isTeacher ? 'Alumno' : 'Profesor';

  const remoteTiles = tiles.filter((t) => !t.isLocal);
  const localTile = tiles.find((t) => t.isLocal);

  /** Ref callback that binds a Chime video tile to its <video> element on mount. */
  const bindTileEl = useCallback(
    (tileId: number) => (el: HTMLVideoElement | null) => {
      if (el) {
        videoElsRef.current.set(tileId, el);
        sessionRef.current?.audioVideo.bindVideoElement(tileId, el);
      } else {
        videoElsRef.current.delete(tileId);
      }
    },
    [],
  );

  const teardown = useCallback(() => {
    try {
      sessionRef.current?.audioVideo.stop();
    } catch {
      /* ignore */
    }
    sessionRef.current = null;
    joiningRef.current = false;
    videoElsRef.current.clear();
    setTiles([]);
  }, []);

  const joinMeeting = useCallback(
    async (meeting: ChimeMeeting, attendee: ChimeAttendee) => {
      const logger = new ConsoleLogger('iqmaxx-session', LogLevel.WARN);
      const deviceController = new DefaultDeviceController(logger);
      const configuration = new MeetingSessionConfiguration(meeting, attendee);
      const meetingSession = new DefaultMeetingSession(configuration, logger, deviceController);
      sessionRef.current = meetingSession;
      const av = meetingSession.audioVideo;

      av.addObserver({
        videoTileDidUpdate: (tileState: VideoTileState) => {
          if (!tileState.boundAttendeeId || tileState.tileId == null || tileState.isContent) return;
          const tileId = tileState.tileId;
          const info: TileInfo = { tileId, isLocal: !!tileState.localTile };
          setTiles((prev) => {
            const idx = prev.findIndex((t) => t.tileId === tileId);
            if (idx === -1) return [...prev, info];
            const next = [...prev];
            next[idx] = info;
            return next;
          });
          const el = videoElsRef.current.get(tileId);
          if (el) av.bindVideoElement(tileId, el);
        },
        videoTileWasRemoved: (tileId: number) => {
          setTiles((prev) => prev.filter((t) => t.tileId !== tileId));
          videoElsRef.current.delete(tileId);
        },
      });

      // STU-10-FR-05: a denied or missing camera or microphone is a reduced
      // way in, not a locked door. Each device starts on its own and a failure
      // is recorded; the room is joined regardless. Being counted present is
      // the provider's join on the server, which no device state touches.
      const local = await startLocalMedia(av, { camera: startWithCamera });
      if (audioRef.current) av.bindAudioElement(audioRef.current);

      av.start();
      if (local.video === 'ON') av.startLocalVideoTile();
      setMedia(local);
      setMuted(av.realtimeIsLocalAudioMuted());
      setCamOff(local.video !== 'ON');
      setStatus('in-call');
    },
    [startWithCamera],
  );

  const loadAndJoin = useCallback(async () => {
    if (!bookingId || joiningRef.current || sessionRef.current) return;
    try {
      const info = await getSessionJoinInfo(bookingId);
      if (!info.meeting || !info.attendee) {
        // Room provisioned but not live yet — keep waiting / let the teacher start.
        setStatus(isTeacher ? 'teacher-start' : 'waiting');
        return;
      }
      // The room IS live. From here a failure is a media/device problem, not a
      // "not started yet" problem — surface it instead of bouncing to waiting.
      joiningRef.current = true;
      setRecordingStatus(info.recordingStatus);
      setStatus('joining');
      setError(null);
      try {
        await joinMeeting(info.meeting, info.attendee);
      } catch (e) {
        teardown();
        setError(
          e instanceof Error
            ? `No pudimos iniciar tu cámara o micrófono: ${e.message}`
            : 'No pudimos acceder a tu cámara o micrófono. Revisa los permisos del navegador.',
        );
        setStatus('device-error');
      }
    } catch (e) {
      // The server names why a learner cannot enter (its clock, the governed
      // window). A closed, cancelled or unavailable class is said as such and
      // not waited on; "the teacher has not opened it" is a wait.
      const refusal = isTeacher ? undefined : joinRefusalOf(e);
      if (refusal && !isWaitingRefusal(refusal)) {
        setError(JOIN_REFUSAL_COPY[refusal]);
        setStatus('error');
        return;
      }
      // Otherwise the session is not live yet. Teacher can start; others wait.
      setStatus(isTeacher ? 'teacher-start' : 'waiting');
    }
  }, [bookingId, isTeacher, joinMeeting, teardown]);

  const handleTeacherStart = useCallback(async () => {
    if (!bookingId) return;
    setStatus('loading');
    setError(null);
    try {
      const res = await startSession(bookingId);
      if (!res.started) {
        setError(res.reason || 'No se pudo iniciar la sesión');
        setStatus('teacher-start');
        return;
      }
      if (res.meeting && res.attendee) {
        joiningRef.current = true;
        setRecordingStatus(res.milestone?.recordingStatus);
        setStatus('joining');
        try {
          await joinMeeting(res.meeting, res.attendee);
        } catch (e) {
          teardown();
          setError(
            e instanceof Error
              ? `No pudimos iniciar tu cámara o micrófono: ${e.message}`
              : 'No pudimos acceder a tu cámara o micrófono. Revisa los permisos del navegador.',
          );
          setStatus('device-error');
        }
      } else {
        await loadAndJoin();
      }
    } catch (e) {
      const refusal = joinRefusalOf(e);
      setError(
        refusal
          ? JOIN_REFUSAL_COPY[refusal]
          : e instanceof Error
          ? e.message
          : 'Error al iniciar la sesión',
      );
      setStatus('teacher-start');
    }
  }, [bookingId, joinMeeting, loadAndJoin, teardown]);

  const toggleMute = useCallback(() => {
    const av = sessionRef.current?.audioVideo;
    if (!av) return;
    if (av.realtimeIsLocalAudioMuted()) {
      av.realtimeUnmuteLocalAudio();
      setMuted(false);
    } else {
      av.realtimeMuteLocalAudio();
      setMuted(true);
    }
  }, []);

  const toggleCamera = useCallback(async () => {
    const av = sessionRef.current?.audioVideo;
    if (!av) return;
    if (camOff) {
      // Entered without a camera: start the device first, and stay in the
      // class with it off if it still cannot start.
      if (media?.video !== 'ON') {
        const video = await startCamera(av);
        setMedia((m) => (m ? { ...m, video } : m));
        if (video !== 'ON') return;
      }
      av.startLocalVideoTile();
      setCamOff(false);
    } else {
      av.stopLocalVideoTile();
      setCamOff(true);
    }
  }, [camOff, media]);

  const leave = useCallback(async () => {
    teardown();
    // The teacher ending the room stops the recording and triggers concatenation.
    if (isTeacher && bookingId) {
      try {
        await endSession(bookingId);
      } catch {
        /* best-effort — the session can also be ended from the enrollment view */
      }
    }
    setStatus('ended');
  }, [bookingId, isTeacher, teardown]);

  useEffect(() => {
    void loadAndJoin();
    return () => {
      teardown();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingId]);

  // While waiting for the teacher to start, re-check every 5s and auto-join once live.
  useEffect(() => {
    if (status !== 'waiting') return;
    const id = setInterval(() => {
      void loadAndJoin();
    }, 5000);
    return () => clearInterval(id);
  }, [status, loadAndJoin]);

  const retryDevices = useCallback(() => {
    setError(null);
    joiningRef.current = false;
    setStatus('loading');
    void loadAndJoin();
  }, [loadAndJoin]);

  return (
    <div style={stageStyle}>
      <style>{sessionRoomCss}</style>

      {/* Top bar */}
      <div style={topBarStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontWeight: 600, fontSize: 15 }}>Sesión en vivo</span>
          {/* DEC-S3-S08-08: "failure de recording no fabrica recording state".
              Only the server's word, and only while actually in the room. */}
          {status === 'in-call' && recordingStatus === 'recording' && (
            <span style={recordingPillStyle}>
              <span className="rec-dot" />
              Grabando
            </span>
          )}
          {status === 'in-call' && recordingStatus === 'failed' && (
            <span style={recordingPillStyle}>Grabación no disponible</span>
          )}
        </div>
        {status === 'in-call' && (
          <span style={{ color: '#9aa0a6', fontSize: 13 }}>
            {remoteTiles.length > 0
              ? `Conectado con ${counterpartLabel}`
              : 'Esperando participante…'}
          </span>
        )}
      </div>

      {guardianEntry && !isTeacher && (
        <div role="note" style={mediaNoticeStyle}>
          Entras como acompañante. La asistencia del alumno cuenta cuando entra desde su propio
          espacio.
        </div>
      )}

      {status === 'in-call' && media && reducedMediaNotice(media) && (
        <div role="status" style={mediaNoticeStyle}>
          {reducedMediaNotice(media)}
        </div>
      )}

      {/* Main content */}
      <div style={contentStyle}>
        {status === 'in-call' ? (
          <VideoGrid
            remoteTiles={remoteTiles}
            localTile={localTile}
            camOff={camOff}
            counterpartLabel={counterpartLabel}
            bindTileEl={bindTileEl}
          />
        ) : (
          <CenterPanel
            status={status}
            error={error}
            counterpartLabel={counterpartLabel}
            onTeacherStart={handleTeacherStart}
            onRetry={retryDevices}
            onBack={() => navigate(backTo)}
            onRejoin={onRejoin}
          />
        )}
      </div>

      <audio ref={audioRef} autoPlay />

      {/* Control bar */}
      {status === 'in-call' && (
        <div style={controlBarStyle}>
          <button
            onClick={toggleMute}
            className="ctrl-btn"
            style={muted ? ctrlBtnActive : ctrlBtnStyle}
            disabled={media?.audio === 'UNAVAILABLE'}
            title={
              media?.audio === 'UNAVAILABLE'
                ? 'Micrófono no disponible'
                : muted
                ? 'Activar micrófono'
                : 'Silenciar micrófono'
            }
          >
            <MicIcon off={muted} />
          </button>
          <button
            onClick={toggleCamera}
            className="ctrl-btn"
            style={camOff ? ctrlBtnActive : ctrlBtnStyle}
            title={camOff ? 'Activar cámara' : 'Apagar cámara'}
          >
            <CamIcon off={camOff} />
          </button>
          <button
            onClick={leave}
            className="ctrl-btn"
            style={leaveBtnStyle}
            title="Salir de la sesión"
          >
            <LeaveIcon />
          </button>
        </div>
      )}
    </div>
  );
};

/* ------------------------------------------------------------------ *
 * Subcomponents
 * ------------------------------------------------------------------ */

const VideoGrid: React.FC<{
  remoteTiles: TileInfo[];
  localTile?: TileInfo;
  camOff: boolean;
  counterpartLabel: string;
  bindTileEl: (tileId: number) => (el: HTMLVideoElement | null) => void;
}> = ({ remoteTiles, localTile, camOff, counterpartLabel, bindTileEl }) => {
  const hasRemote = remoteTiles.length > 0;
  const cols = remoteTiles.length <= 1 ? 1 : 2;

  return (
    <>
      {hasRemote ? (
        <div
          className="video-grid"
          style={{ ...gridStyle, gridTemplateColumns: `repeat(${cols}, 1fr)` }}
        >
          {remoteTiles.map((t) => (
            <div key={t.tileId} style={tileCardStyle}>
              <video ref={bindTileEl(t.tileId)} autoPlay playsInline style={tileVideoStyle} />
              <span style={tileLabelStyle}>{counterpartLabel}</span>
            </div>
          ))}
        </div>
      ) : (
        <div style={waitingRemoteStyle}>
          <Avatar label={counterpartLabel} size={96} />
          <p style={{ color: '#c9cdd3', marginTop: 16, fontSize: 15 }}>
            {counterpartLabel} está conectándose…
          </p>
        </div>
      )}

      {/* Self-view picture-in-picture */}
      {localTile && (
        <div style={pipStyle} className="self-pip">
          {camOff ? (
            <div style={pipCamOffStyle}>
              <Avatar label="Tú" size={40} />
            </div>
          ) : (
            <video
              ref={bindTileEl(localTile.tileId)}
              autoPlay
              playsInline
              muted
              className="mirror"
              style={pipVideoStyle}
            />
          )}
          <span style={pipLabelStyle}>Tú</span>
        </div>
      )}
    </>
  );
};

const CenterPanel: React.FC<{
  status: Status;
  error: string | null;
  counterpartLabel: string;
  onTeacherStart: () => void;
  onRetry: () => void;
  onBack: () => void;
  onRejoin?: () => void;
}> = ({ status, error, counterpartLabel, onTeacherStart, onRetry, onBack, onRejoin }) => {
  return (
    <div style={centerCardStyle}>
      {error && status !== 'device-error' && status !== 'error' && (
        <div style={errorStyle}>{error}</div>
      )}

      {(status === 'loading' || status === 'joining') && (
        <>
          <div className="spinner" style={spinnerStyle} />
          <p style={centerTextStyle}>
            {status === 'joining' ? 'Entrando a la sesión…' : 'Conectando…'}
          </p>
        </>
      )}

      {status === 'teacher-start' && (
        <>
          <Avatar label={counterpartLabel} size={80} />
          <h2 style={centerTitleStyle}>Todo listo para comenzar</h2>
          <p style={centerTextStyle}>
            La sala aún no está activa. Inicia la sesión para comenzar la grabación.
          </p>
          <button onClick={onTeacherStart} style={primaryButtonStyle} className="primary-btn">
            Iniciar sesión
          </button>
        </>
      )}

      {status === 'waiting' && (
        <>
          <div className="pulse-ring" style={pulseRingStyle}>
            <Avatar label={counterpartLabel} size={72} />
          </div>
          <h2 style={centerTitleStyle}>{JOIN_REFUSAL_COPY.WAITING_FOR_TEACHER}</h2>
          <p style={centerTextStyle}>
            En cuanto el profesor inicie la sesión, te conectaremos automáticamente.
          </p>
        </>
      )}

      {status === 'device-error' && (
        <>
          <div style={errorStyle}>{error || 'No pudimos acceder a tu cámara o micrófono.'}</div>
          <p style={centerTextStyle}>
            La sesión ya está activa. Revisa los permisos de cámara y micrófono del navegador e
            inténtalo de nuevo.
          </p>
          <button onClick={onRetry} style={primaryButtonStyle} className="primary-btn">
            Reintentar
          </button>
        </>
      )}

      {status === 'ended' && (
        <>
          <h2 style={centerTitleStyle}>Has salido de la sesión</h2>
          {onRejoin && (
            <button onClick={onRejoin} style={primaryButtonStyle} className="primary-btn">
              Volver a entrar
            </button>
          )}
          <button onClick={onBack} style={primaryButtonStyle} className="primary-btn">
            Volver
          </button>
        </>
      )}

      {status === 'error' && (
        <>
          <div style={errorStyle}>{error || 'Ocurrió un error.'}</div>
          {onRejoin && (
            <button onClick={onRejoin} style={primaryButtonStyle} className="primary-btn">
              Volver a entrar
            </button>
          )}
          <button onClick={onBack} style={primaryButtonStyle} className="primary-btn">
            Volver
          </button>
        </>
      )}
    </div>
  );
};

const Avatar: React.FC<{ label: string; size: number }> = ({ label, size }) => (
  <div
    style={{
      width: size,
      height: size,
      borderRadius: '50%',
      background: 'linear-gradient(135deg, #4c6ef5, #1971c2)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#fff',
      fontWeight: 700,
      fontSize: size * 0.4,
      flexShrink: 0,
    }}
  >
    {label.charAt(0).toUpperCase()}
  </div>
);

/* ------------------------------------------------------------------ *
 * Icons (inline SVG — no external deps)
 * ------------------------------------------------------------------ */

const MicIcon: React.FC<{ off?: boolean }> = ({ off }) => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
    <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
    <line x1="12" y1="19" x2="12" y2="23" />
    {off && <line x1="2" y1="2" x2="22" y2="22" stroke="currentColor" />}
  </svg>
);

const CamIcon: React.FC<{ off?: boolean }> = ({ off }) => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M23 7l-7 5 7 5V7z" />
    <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
    {off && <line x1="2" y1="2" x2="22" y2="22" stroke="currentColor" />}
  </svg>
);

const LeaveIcon: React.FC = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
    <path d="M21 15.46l-5.27-.61-2.52 2.52a15.05 15.05 0 0 1-6.59-6.59l2.53-2.53L8.54 3H3.03C2.45 13.18 10.82 21.55 21 20.97v-5.51z" />
  </svg>
);

/* ------------------------------------------------------------------ *
 * Styles
 * ------------------------------------------------------------------ */

const stageStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: '#202124',
  color: '#e8eaed',
  display: 'flex',
  flexDirection: 'column',
  zIndex: 1000,
  fontFamily: 'inherit',
};

const topBarStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '14px 24px',
  flexShrink: 0,
};

const recordingPillStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  background: 'rgba(217, 48, 37, 0.15)',
  color: '#f28b82',
  borderRadius: 999,
  padding: '4px 12px',
  fontSize: 12,
  fontWeight: 600,
};

const contentStyle: React.CSSProperties = {
  flex: 1,
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '0 24px',
  minHeight: 0,
  overflow: 'hidden',
};

const gridStyle: React.CSSProperties = {
  display: 'grid',
  gap: 16,
  width: '100%',
  maxWidth: 1280,
  maxHeight: '100%',
};

const tileCardStyle: React.CSSProperties = {
  position: 'relative',
  background: '#3c4043',
  borderRadius: 12,
  overflow: 'hidden',
  aspectRatio: '16 / 9',
  boxShadow: '0 4px 20px rgba(0,0,0,0.35)',
};

const tileVideoStyle: React.CSSProperties = {
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  background: '#000',
};

const tileLabelStyle: React.CSSProperties = {
  position: 'absolute',
  bottom: 10,
  left: 12,
  background: 'rgba(0,0,0,0.55)',
  color: '#fff',
  padding: '3px 10px',
  borderRadius: 6,
  fontSize: 13,
  fontWeight: 500,
};

const waitingRemoteStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
};

const pipStyle: React.CSSProperties = {
  position: 'absolute',
  bottom: 20,
  right: 28,
  width: 220,
  aspectRatio: '16 / 9',
  background: '#3c4043',
  borderRadius: 12,
  overflow: 'hidden',
  boxShadow: '0 6px 24px rgba(0,0,0,0.5)',
  border: '1px solid rgba(255,255,255,0.1)',
};

const pipVideoStyle: React.CSSProperties = {
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  background: '#000',
};

const pipCamOffStyle: React.CSSProperties = {
  width: '100%',
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: '#3c4043',
};

const pipLabelStyle: React.CSSProperties = {
  position: 'absolute',
  bottom: 6,
  left: 8,
  background: 'rgba(0,0,0,0.55)',
  color: '#fff',
  padding: '2px 8px',
  borderRadius: 5,
  fontSize: 11,
  fontWeight: 500,
};

const centerCardStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
  maxWidth: 420,
};

const centerTitleStyle: React.CSSProperties = {
  fontSize: 22,
  fontWeight: 600,
  margin: '20px 0 8px',
};

const centerTextStyle: React.CSSProperties = {
  color: '#9aa0a6',
  fontSize: 15,
  lineHeight: 1.5,
  margin: '4px 0 20px',
};

const controlBarStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 16,
  padding: '18px 0 26px',
  flexShrink: 0,
};

const ctrlBtnStyle: React.CSSProperties = {
  width: 56,
  height: 56,
  borderRadius: '50%',
  border: 'none',
  background: '#3c4043',
  color: '#e8eaed',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  transition: 'background 0.15s ease',
};

const ctrlBtnActive: React.CSSProperties = {
  ...ctrlBtnStyle,
  background: '#e8eaed',
  color: '#202124',
};

const leaveBtnStyle: React.CSSProperties = {
  ...ctrlBtnStyle,
  width: 64,
  background: '#d93025',
  color: '#fff',
};

const spinnerStyle: React.CSSProperties = {
  width: 44,
  height: 44,
  borderRadius: '50%',
  border: '3px solid rgba(255,255,255,0.15)',
  borderTopColor: '#4c6ef5',
};

const pulseRingStyle: React.CSSProperties = {
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const mediaNoticeStyle: React.CSSProperties = {
  margin: '0 24px 8px',
  padding: '8px 12px',
  borderRadius: 8,
  background: 'rgba(76, 110, 245, 0.18)',
  color: '#e8eaed',
  fontSize: 14,
};

const errorStyle: React.CSSProperties = {
  background: 'rgba(217, 48, 37, 0.15)',
  border: '1px solid rgba(242, 139, 130, 0.4)',
  borderRadius: 8,
  padding: 12,
  color: '#f28b82',
  marginBottom: 12,
  fontSize: 14,
};

const primaryButtonStyle: React.CSSProperties = {
  padding: '12px 28px',
  backgroundColor: '#1971c2',
  color: '#fff',
  border: 'none',
  borderRadius: 8,
  fontSize: 15,
  fontWeight: 600,
  cursor: 'pointer',
};

const sessionRoomCss = `
  @keyframes iqmx-spin { to { transform: rotate(360deg); } }
  @keyframes iqmx-blink { 0%, 100% { opacity: 1; } 50% { opacity: 0.25; } }
  @keyframes iqmx-pulse {
    0% { box-shadow: 0 0 0 0 rgba(76, 110, 245, 0.45); }
    70% { box-shadow: 0 0 0 24px rgba(76, 110, 245, 0); }
    100% { box-shadow: 0 0 0 0 rgba(76, 110, 245, 0); }
  }
  .spinner { animation: iqmx-spin 0.9s linear infinite; }
  .rec-dot {
    width: 8px; height: 8px; border-radius: 50%; background: #f28b82;
    display: inline-block; animation: iqmx-blink 1.4s ease-in-out infinite;
  }
  .pulse-ring { border-radius: 50%; animation: iqmx-pulse 2s infinite; }
  .mirror { transform: scaleX(-1); }
  .ctrl-btn:hover { filter: brightness(1.15); }
  .primary-btn:hover { filter: brightness(1.08); }
  @media (max-width: 640px) {
    .video-grid { grid-template-columns: 1fr !important; }
    .self-pip { width: 130px !important; bottom: 14px !important; right: 14px !important; }
  }
`;

export default SessionRoom;
